import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/brand/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import QuoteDialog from "@/components/quotes/QuoteDialog";
import QuoteActionsDialog from "@/components/quotes/QuoteActionsDialog";
import { FileText, Loader2, Search, Download, Pencil, ExternalLink, Send, CheckCircle2, Zap, Sun, AlertTriangle } from "lucide-react";
import { QUOTE_STATUS, QUOTE_PRODUCTS, quoteStatusTone, eur, downloadQuotePdf } from "@/lib/crm";

export default function QuotesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [product, setProduct] = useState("");
  const [followup, setFollowup] = useState("");
  const [search, setSearch] = useState("");
  const [officeId, setOfficeId] = useState("");
  const [operatorId, setOperatorId] = useState("");
  const [offices, setOffices] = useState([]);
  const [operators, setOperators] = useState([]);
  const [edit, setEdit] = useState(null);
  const [action, setAction] = useState(null);

  useEffect(() => {
    api.get("/offices").then(({ data }) => setOffices(data)).catch(() => {});
    if (isAdmin) api.get("/users").then(({ data }) => setOperators(data.filter((u) => u.active))).catch(() => {});
  }, [isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (category) params.category = category;
      if (product) params.product = product;
      if (followup) params.followup = followup;
      if (search) params.search = search;
      if (officeId) params.office_id = officeId;
      if (operatorId) params.operator_id = operatorId;
      const { data } = await api.get("/quotes", { params });
      setRows(data);
    } catch (e) { setRows([]); } finally { setLoading(false); }
  }, [status, category, product, followup, search, officeId, operatorId]);

  useEffect(() => { const t = setTimeout(load, search ? 250 : 0); return () => clearTimeout(t); }, [load]);

  const pdf = async (q) => { try { await downloadQuotePdf(api, q.id, q.quote_number); } catch (e) { /* ignore */ } };
  const closed = (s) => ["Accettato", "Rifiutato", "Scaduto"].includes(s);
  const attention = rows.filter((q) => q.follow_up_overdue || (!q.follow_up_date && !closed(q.status) && q.status === "Inviato"));

  return (
    <div className="animate-fade-in-up space-y-5" data-testid="quotes-page">
      <div>
        <h1 className="ds-page-title flex items-center gap-2"><FileText className="h-6 w-6 text-brand" /> Preventivi</h1>
        <p className="mt-1 text-sm text-slate-500">Preventivi commodity ed efficienza, con numero automatico, stato, invio, esito e follow-up.</p>
      </div>

      {attention.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-warning/25 bg-warning/10 px-3 py-2.5 text-sm text-warning" data-testid="quotes-attention">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span><strong>{attention.length}</strong> preventivi richiedono attenzione (follow-up scaduto o mancante).</span>
        </div>
      )}

      <div className="ds-card flex flex-wrap items-end gap-3 p-4">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Cerca numero o cliente…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="quotes-search" />
        </div>
        <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[150px]" data-testid="quotes-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutte le categorie</SelectItem><SelectItem value="Commodity">Commodity</SelectItem><SelectItem value="Efficienza">Efficienza</SelectItem></SelectContent>
        </Select>
        <Select value={product || "all"} onValueChange={(v) => setProduct(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[150px]" data-testid="quotes-product"><SelectValue placeholder="Prodotto" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutti i prodotti</SelectItem>{QUOTE_PRODUCTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[140px]" data-testid="quotes-status"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutti gli stati</SelectItem>{QUOTE_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={followup || "all"} onValueChange={(v) => setFollowup(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px]" data-testid="quotes-followup-filter"><SelectValue placeholder="Follow-up" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutti</SelectItem><SelectItem value="overdue">Follow-up scaduto</SelectItem><SelectItem value="missing">Senza follow-up</SelectItem></SelectContent>
        </Select>
        {isAdmin && (
          <>
            <Select value={officeId || "all"} onValueChange={(v) => setOfficeId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[140px]" data-testid="quotes-office"><SelectValue placeholder="Sede" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="all">Tutte le sedi</SelectItem>{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={operatorId || "all"} onValueChange={(v) => setOperatorId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]" data-testid="quotes-operator"><SelectValue placeholder="Consulente" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="all">Tutti</SelectItem>{operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </>
        )}
      </div>

      <div className="ds-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-slate-500"><FileText className="h-8 w-8 text-slate-300" /> Nessun preventivo. Crea un preventivo dalla scheda cliente, da un'opportunità o da un contratto.</div>
        ) : (
          <div className="ds-table-scroll">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-slate-50 text-left">
                <tr className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Numero</th><th className="px-4 py-3">Cliente</th><th className="px-4 py-3">Categoria</th>
                  <th className="px-4 py-3">Prodotto</th><th className="px-4 py-3">Totale</th><th className="px-4 py-3">Stato</th>
                  <th className="px-4 py-3">Inviato</th><th className="px-4 py-3">Follow-up</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((q) => (
                  <tr key={q.id} className="hover:bg-brand/5" data-testid={`quote-row-${q.id}`}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-700">{q.quote_number}</td>
                    <td className="px-4 py-3 font-semibold text-slate-800">{q.customer_name}</td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-slate-600">{q.category === "Commodity" ? <Zap className="h-3.5 w-3.5 text-warning" /> : <Sun className="h-3.5 w-3.5 text-brand" />}{q.category}</span></td>
                    <td className="px-4 py-3 text-slate-600">{q.product || "—"}</td>
                    <td className="px-4 py-3 text-slate-700">{eur(q.final_amount ?? q.amount)}</td>
                    <td className="px-4 py-3"><StatusBadge type={quoteStatusTone(q.status)}>{q.status}</StatusBadge></td>
                    <td className="px-4 py-3 text-slate-600">{q.sent_date ? `${new Date(q.sent_date).toLocaleDateString("it-IT")}${q.days_since_sent != null ? ` (${q.days_since_sent}g)` : ""}` : "—"}</td>
                    <td className="px-4 py-3">{q.follow_up_date ? <span className={q.follow_up_overdue ? "font-semibold text-danger" : "text-slate-600"}>{new Date(q.follow_up_date).toLocaleDateString("it-IT")}</span> : <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {!closed(q.status) && <Button size="sm" variant="ghost" onClick={() => setAction({ quote: q, mode: "send" })} title="Segna come inviato" data-testid={`quote-send-${q.id}`}><Send className="h-4 w-4" /></Button>}
                        {!closed(q.status) && <Button size="sm" variant="ghost" onClick={() => setAction({ quote: q, mode: "outcome" })} title="Registra esito" data-testid={`quote-outcome-${q.id}`}><CheckCircle2 className="h-4 w-4" /></Button>}
                        <Button size="sm" variant="ghost" onClick={() => pdf(q)} title="Scarica PDF" data-testid={`quote-pdf-${q.id}`}><Download className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setEdit(q)} title="Modifica" data-testid={`quote-edit-${q.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/clienti/${q.customer_id}`)} title="Apri cliente"><ExternalLink className="h-4 w-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {edit && <QuoteDialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)} customerId={edit.customer_id} quote={edit} onSaved={() => { setEdit(null); load(); }} />}
      {action && <QuoteActionsDialog open={!!action} onOpenChange={(v) => !v && setAction(null)} quote={action.quote} mode={action.mode} onSaved={() => { setAction(null); load(); }} />}
    </div>
  );
}
